import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Badge, Button, Card, Select } from '../../components/ui';
import { api, ApiError } from '../../lib/api';

const CHECK_TYPES = ['ingredient', 'supplement_facts', 'label', 'claims', 'workflow'] as const;
type CheckType = (typeof CHECK_TYPES)[number];
const FINAL = ['codified', 'final_effective'];

interface RefreshState {
  running: boolean;
  runId: string | null;
  publishing: string | null;
  geminiConfigured: boolean;
  model: string;
  run: {
    status: string;
    startedByEmail: string;
    createdAt: string;
    finishedAt: string | null;
    summary: Record<string, number | string>;
    log: string[];
    error: string | null;
  } | null;
}
interface KbUpdate {
  id: string;
  action: string;
  source: string;
  title: string;
  citation: string;
  summary: string;
  primaryUrl: string | null;
  path: string | null;
  selectedIn: CheckType[];
  proposedSelection: CheckType[];
  sourceStatus: string;
  sourceNote: string | null;
  contentBytes: number | null;
  rule: {
    kind: string;
    name: string;
    synonyms: string[];
    category: string;
    namesMissing: string[];
    sourceQuote: string | null;
  } | null;
  acceptable: boolean;
  blockReason: string | null;
  status: string;
  lastError?: string | null;
  publishedVersion?: string;
  acceptedAt?: string;
  preview: string | null;
}
export interface ActiveInfo {
  version: string;
  fromSeed: boolean;
  budget: { maxFiles: number; maxBytes: number };
}
type Selection = Record<
  string,
  { paths: string[]; selected: number; selectedBytes: number; unresolved: string[] }
>;

export function KbUpdatesPanel({
  active,
  byCheckType,
  onPublished,
}: {
  active: ActiveInfo;
  byCheckType: Selection;
  onPublished: () => void;
}) {
  const { t } = useTranslation();
  const [rs, setRs] = useState<RefreshState | null>(null);
  const [updates, setUpdates] = useState<KbUpdate[]>([]);
  const [msg, setMsg] = useState<{ tone: 'green' | 'red' | 'amber'; text: string } | null>(null);
  const [showLog, setShowLog] = useState(false);

  const load = useCallback(async () => {
    const [s, u] = await Promise.all([
      api<RefreshState>('/api/v1/superadmin/kb/refresh'),
      api<{ updates: KbUpdate[] }>('/api/v1/superadmin/kb/updates'),
    ]);
    setRs(s);
    setUpdates(u.updates);
    return { s, u: u.updates };
  }, []);
  useEffect(() => {
    void load();
  }, [load]);
  const busy = !!rs?.running || updates.some((u) => u.status === 'publishing');
  useEffect(() => {
    if (!busy) return;
    const id = setInterval(() => {
      void load().then(({ u }) => {
        if (!u.some((x) => x.status === 'publishing')) onPublished();
      });
    }, 4000);
    return () => clearInterval(id);
  }, [busy, load, onPublished]);

  const start = async () => {
    setMsg(null);
    try {
      await api('/api/v1/superadmin/kb/refresh', { method: 'POST', json: {} });
      await load();
    } catch (e) {
      setMsg({ tone: 'red', text: (e as Error).message });
    }
  };
  const pending = updates.filter((u) => u.status === 'pending_review' || u.status === 'publishing');
  const accepted = updates.filter((u) => u.status === 'accepted').slice(0, 10);
  const unresolved = Object.entries(byCheckType).flatMap(([k, v]) => v.unresolved.map((p) => `${k}: ${p}`));

  return (
    <Card title={t('sakb.refresh.title')}>
      <div className="space-y-3 text-sm">
        <div className="flex flex-wrap items-center gap-3">
          <span>
            {t('sakb.refresh.active')}: <span className="font-mono">{active.version}</span>{' '}
            {active.fromSeed && <Badge tone="slate">{t('sakb.refresh.fromSeed')}</Badge>}
          </span>
          <Badge tone={rs?.geminiConfigured ? 'green' : 'amber'}>
            {rs?.geminiConfigured ? rs.model : t('sakb.refresh.noGemini')}
          </Badge>
          <Button className="ml-auto" disabled={busy} onClick={() => void start()}>
            {rs?.running ? t('sakb.refresh.running') : t('sakb.refresh.check')}
          </Button>
        </div>
        <p className="text-xs text-slate-500">{t('sakb.refresh.explain')}</p>
        {msg && <Alert tone={msg.tone}>{msg.text}</Alert>}
        {unresolved.length > 0 && (
          <Alert tone="amber">
            {t('sakb.refresh.unresolved')}: <span className="font-mono">{unresolved.join(', ')}</span>
          </Alert>
        )}
        {rs?.run && (
          <div className="rounded border border-slate-200 p-2 text-xs">
            <div className="flex flex-wrap gap-3">
              <Badge tone={rs.run.status === 'done' ? 'green' : rs.run.status === 'failed' ? 'red' : 'blue'}>
                {rs.run.status}
              </Badge>
              <span>
                {rs.run.createdAt.slice(0, 16).replace('T', ' ')} · {rs.run.startedByEmail}
              </span>
              {Object.entries(rs.run.summary).map(([k, v]) => (
                <span key={k} className="text-slate-600">
                  {k}: <b>{String(v)}</b>
                </span>
              ))}
              <button className="text-emerald-700 underline" onClick={() => setShowLog((x) => !x)}>
                {t('sakb.refresh.log')}
              </button>
            </div>
            {rs.run.error && <p className="mt-1 text-red-600">{rs.run.error}</p>}
            {showLog && (
              <pre className="mt-2 whitespace-pre-wrap text-slate-600">{rs.run.log.join('\n')}</pre>
            )}
          </div>
        )}
        {pending.length === 0 ? (
          <p className="text-slate-500">{t('sakb.refresh.none')}</p>
        ) : (
          <ul className="divide-y divide-slate-100">
            {pending.map((u) => (
              <UpdateRow
                key={u.id}
                u={u}
                byCheckType={byCheckType}
                budget={active.budget}
                onDone={async (text, tone) => {
                  setMsg({ text, tone });
                  await load();
                }}
              />
            ))}
          </ul>
        )}
        {accepted.length > 0 && (
          <details className="text-xs">
            <summary className="cursor-pointer text-slate-600">{t('sakb.refresh.accepted')}</summary>
            <ul className="mt-1 space-y-1">
              {accepted.map((u) => (
                <li key={u.id}>
                  <span className="font-mono">{u.publishedVersion}</span> · {u.action} ·{' '}
                  {u.citation || u.title}
                </li>
              ))}
            </ul>
          </details>
        )}
      </div>
    </Card>
  );
}

function UpdateRow({
  u,
  byCheckType,
  budget,
  onDone,
}: {
  u: KbUpdate;
  byCheckType: Selection;
  budget: { maxFiles: number; maxBytes: number };
  onDone: (text: string, tone: 'green' | 'red' | 'amber') => Promise<void>;
}) {
  const { t } = useTranslation();
  const [sel, setSel] = useState<CheckType[]>(u.action === 'add_file' ? u.proposedSelection : u.selectedIn);
  const [swap, setSwap] = useState<Record<string, string>>({});
  const [severity, setSeverity] = useState<'major' | 'critical' | 'info'>('major');
  const [attest, setAttest] = useState(false);
  const [open, setOpen] = useState(false);
  const [working, setWorking] = useState(false);
  const selectable = u.action === 'add_file';
  const drives =
    u.action === 'add_rule' || (u.action === 'refresh_verbatim' ? u.selectedIn.length > 0 : sel.length > 0);
  const needAttest = drives && !FINAL.includes(u.sourceStatus);
  const overFor = (ct: CheckType) => {
    const s = byCheckType[ct];
    if (!s || !u.path || s.paths.includes(u.path)) return false;
    const files = s.selected + 1 - (swap[ct] ? 1 : 0);
    const bytes = s.selectedBytes + (u.contentBytes ?? 0);
    return files > budget.maxFiles || bytes > budget.maxBytes;
  };
  const accept = async () => {
    setWorking(true);
    try {
      await api(`/api/v1/superadmin/kb/updates/${u.id}/accept`, {
        method: 'POST',
        json: {
          ...(selectable ? { selection: sel } : {}),
          ...(Object.keys(swap).length ? { swapOut: swap } : {}),
          ...(u.action === 'add_rule' ? { severity } : {}),
          ...(needAttest ? { attestFinal: attest } : {}),
        },
      });
      await onDone(t('sakb.refresh.publishing'), 'green');
    } catch (e) {
      await onDone(e instanceof ApiError ? `${e.code}: ${e.message}` : (e as Error).message, 'red');
    } finally {
      setWorking(false);
    }
  };
  const dismiss = async () => {
    setWorking(true);
    try {
      await api(`/api/v1/superadmin/kb/updates/${u.id}/dismiss`, { method: 'POST', json: {} });
      await onDone(t('sakb.refresh.dismissed'), 'amber');
    } finally {
      setWorking(false);
    }
  };
  const statusTone = FINAL.includes(u.sourceStatus)
    ? 'green'
    : u.sourceStatus === 'unverified'
      ? 'amber'
      : 'red';
  return (
    <li className="space-y-2 py-3">
      <div className="flex flex-wrap items-center gap-2">
        <Badge tone="blue">{u.action}</Badge>
        <Badge tone={statusTone}>{u.sourceStatus}</Badge>
        <Badge tone="slate">{u.source}</Badge>
        <span className="font-medium">{u.title}</span>
        {u.citation && <span className="font-mono text-xs text-slate-500">{u.citation}</span>}
        {u.status === 'publishing' && <Badge tone="blue">{t('sakb.refresh.publishingShort')}</Badge>}
      </div>
      <p className="text-xs text-slate-600">{u.summary}</p>
      <div className="flex flex-wrap gap-3 text-xs text-slate-500">
        {u.path && <span className="font-mono">{u.path}</span>}
        {u.primaryUrl && (
          <a className="text-emerald-700 underline" href={u.primaryUrl} target="_blank" rel="noreferrer">
            {t('sakb.refresh.source')}
          </a>
        )}
        {u.contentBytes != null && <span>{Math.round(u.contentBytes / 1024)} KB</span>}
        {u.sourceNote && <span>{u.sourceNote}</span>}
        {u.preview && (
          <button className="text-emerald-700 underline" onClick={() => setOpen((x) => !x)}>
            {t('sakb.refresh.preview')}
          </button>
        )}
      </div>
      {open && u.preview && (
        <pre className="max-h-64 overflow-auto whitespace-pre-wrap rounded bg-slate-50 p-2 text-xs">
          {u.preview}
        </pre>
      )}
      {u.rule && (
        <p className="text-xs">
          {t('sakb.refresh.rule')}: <b>{u.rule.name}</b> ({u.rule.kind}, {u.rule.category})
          {u.rule.synonyms.length > 0 && ` · ${u.rule.synonyms.join(', ')}`}
          {u.rule.sourceQuote && <span className="block italic text-slate-500">“{u.rule.sourceQuote}”</span>}
        </p>
      )}
      {u.lastError && <Alert tone="red">{u.lastError}</Alert>}
      {!u.acceptable ? (
        <div className="flex items-center gap-3">
          <span className="text-xs text-amber-700">{u.blockReason}</span>
          <Button variant="ghost" disabled={working} onClick={() => void dismiss()}>
            {t('sakb.refresh.dismiss')}
          </Button>
        </div>
      ) : (
        u.status === 'pending_review' && (
          <div className="space-y-2 rounded border border-slate-200 p-2 text-xs">
            {selectable && (
              <div className="flex flex-wrap items-center gap-3">
                <span>{t('sakb.refresh.useFor')}:</span>
                {CHECK_TYPES.map((ct) => (
                  <label key={ct} className="flex items-center gap-1">
                    <input
                      type="checkbox"
                      checked={sel.includes(ct)}
                      onChange={(e) =>
                        setSel((s) => (e.target.checked ? [...s, ct] : s.filter((x) => x !== ct)))
                      }
                    />
                    <span className="font-mono">{ct}</span>
                  </label>
                ))}
              </div>
            )}
            {selectable &&
              sel.filter(overFor).map((ct) => (
                <div key={ct} className="flex items-center gap-2">
                  <span className="text-amber-700">{t('sakb.refresh.full', { type: ct })}</span>
                  <Select
                    value={swap[ct] ?? ''}
                    onChange={(e) => setSwap((s) => ({ ...s, [ct]: e.target.value }))}
                  >
                    <option value="">{t('sakb.refresh.swapOut')}</option>
                    {byCheckType[ct]?.paths.map((p) => (
                      <option key={p} value={p}>
                        {p}
                      </option>
                    ))}
                  </Select>
                </div>
              ))}
            {u.action === 'add_rule' && (
              <label className="flex items-center gap-2">
                {t('sakb.refresh.severity')}
                <Select value={severity} onChange={(e) => setSeverity(e.target.value as typeof severity)}>
                  <option value="major">major</option>
                  <option value="critical">critical</option>
                  <option value="info">info</option>
                </Select>
              </label>
            )}
            {needAttest && (
              <label className="flex items-center gap-2 text-amber-800">
                <input type="checkbox" checked={attest} onChange={(e) => setAttest(e.target.checked)} />
                {t('sakb.refresh.attest')}
              </label>
            )}
            <p className="text-slate-500">
              {drives ? t('sakb.refresh.willChange') : t('sakb.refresh.folderOnly')}
            </p>
            <div className="flex gap-2">
              <Button disabled={working || (needAttest && !attest)} onClick={() => void accept()}>
                {t('sakb.refresh.accept')}
              </Button>
              <Button variant="ghost" disabled={working} onClick={() => void dismiss()}>
                {t('sakb.refresh.dismiss')}
              </Button>
            </div>
          </div>
        )
      )}
    </li>
  );
}
